import axios, { AxiosInstance, AxiosResponse } from 'axios'
import qs from 'qs'
import * as models from './models'
import AuthToken from '@/auth/auth-token'

class Document {
  protected readonly document: AxiosInstance;

  constructor () {
    const document = axios.create({
      baseURL: process.env.VUE_APP_BASE_API,
      withCredentials: false,
      headers: {
        'Content-type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept'
      }
    })

    this.document = document
  }

  handleSuccess (response: AxiosResponse) {
    return response
  }

  public post = async (data: models.Document) => {
    await AuthToken.setAuth(this.document)

    return this.document({
      url: 'v1/doc',
      method: 'POST',
      data: data
    })
  }

  public put = async (docId: string, data: models.Document) => {
    await AuthToken.setAuth(this.document)

    return this.document({
      url: `v1/doc/${docId}`,
      method: 'PUT',
      data: data
    })
  }

  public get = async (docId: string) => {
    await AuthToken.setAuth(this.document)

    return this.document({
      url: `v1/doc/${docId}`,
      method: 'GET'
    })
  }

  // `data` is the POST body. Supported keys: party_ids, excluded_subcategories,
  // filter_text, and the optional `start_date` / `end_date` (YYYY-MM-DD) bounds
  // that filter on the document `created_on` (portal ingest date); the backend
  // treats the range as inclusive of the full end day. Absent date fields mean
  // no date filter is applied.
  public search = async (data: Record<string, any>, tags: any[], offset: number, limit: number) => {
    await AuthToken.setAuth(this.document)

    return this.document({
      url: 'v1/docs/search',
      method: 'POST',
      data,
      params: {
        root_key: 'ipro_portal',
        tags: JSON.stringify(tags),
        offset: offset || 0,
        limit: limit || 10,
      },
      paramsSerializer: params => {
        return qs.stringify(params, { indices: false, encode: false })
      }
    })
  }

  public delete = async (docId: string) => {
    await AuthToken.setAuth(this.document)

    return this.document({
      url: `v1/doc/${docId}`,
      method: 'DELETE'
    })
  }

  // Requests a single ZIP archive of the selected documents' files. The backend
  // streams `application/zip`; the filename is carried in Content-Disposition and
  // the count of selected docs that had no file in the `X-Missing-Documents`
  // header (both CORS-exposed by the API). Over the server cap → HTTP 400.
  public downloadBulk = async (docIds: string[]) => {
    await AuthToken.setAuth(this.document)

    return this.document({
      url: 'v1/docs/download',
      method: 'POST',
      data: { doc_ids: docIds },
      responseType: 'blob'
    })
  }

  public downloadCreditRequestTemplate = async () => {
    await AuthToken.setAuth(this.document)

    return this.document({
      url: `v1/doc/download/template`,
      method: 'GET',
    })
  }

  public upload = async (userEmail: string, data: FormData, tags: any[]) => {
    await AuthToken.setAuth(this.document)

    return this.document({
      url: `v1/doc/credit_request_form/upload`,
      method: 'POST',
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: { user_email: userEmail, tags: JSON.stringify(tags) },
      data: data
    })
  }
}

export default new Document()
